import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/layout/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, Mail, BookOpen, LifeBuoy, MessageSquare } from 'lucide-react';
import LoadingSpinner from '../components/common/LoadingSpinner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactUsPage = () => {
  const [supportEmail, setSupportEmail] = useState('');
  const [supportFormUrl, setSupportFormUrl] = useState('');
  const [contactFormUrl, setContactFormUrl] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        // Pull from BOTH sources in parallel — email_settings (support ticket
        // form, seeded by admin) and the page-builder external-links doc
        // (general "contact us" form, may pre-date the ticket form).
        const [supportRes, extRes] = await Promise.all([
          axios.get(`${API}/email/public/support-contacts`).catch(() => ({ data: {} })),
          axios.get(`${API}/pages/external-links`).catch(() => ({ data: {} })),
        ]);
        if (!alive) return;
        setSupportEmail((supportRes.data.support_email || '').trim());
        setSupportFormUrl((supportRes.data.support_form_url || '').trim());
        setContactFormUrl((extRes.data.contact_us_url || '').trim());
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center min-h-[400px]">
          <LoadingSpinner />
        </div>
      </Layout>
    );
  }

  // Build the tile list dynamically so single-URL setups render cleanly
  // (2x2 when both forms present, 1x3 when one is missing, 1x2 when neither).
  const tiles = [
    {
      key: 'help',
      to: '/help',
      icon: BookOpen,
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
      title: 'Browse Help Guide',
      subtitle: 'Answers to how-to and policy questions',
      testId: 'contact-help-tile',
    },
    {
      key: 'email',
      href: supportEmail ? `mailto:${supportEmail}?subject=Support%20request` : null,
      icon: Mail,
      iconBg: 'bg-teal-100',
      iconColor: 'text-teal-600',
      title: 'Email Us',
      subtitle: supportEmail || 'Email address coming soon',
      subtitleClass: supportEmail ? 'break-all' : '',
      disabled: !supportEmail,
      testId: 'contact-email-tile',
    },
  ];

  if (supportFormUrl) {
    tiles.push({
      key: 'support-ticket',
      href: supportFormUrl,
      external: true,
      icon: LifeBuoy,
      iconBg: 'bg-orange-100',
      iconColor: 'text-orange-600',
      title: 'Support Ticket',
      subtitle: 'For account, billing, or technical issues',
      testId: 'contact-ticket-tile',
    });
  }

  if (contactFormUrl) {
    tiles.push({
      key: 'general-inquiry',
      href: contactFormUrl,
      external: true,
      icon: MessageSquare,
      iconBg: 'bg-purple-100',
      iconColor: 'text-purple-600',
      title: 'General Inquiry',
      subtitle: 'Feedback, partnership, or general questions',
      testId: 'contact-inquiry-tile',
    });
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        <Link to="/dashboard">
          <Button variant="ghost" className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </Link>

        {/* Hero */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Contact Us</h1>
          <p className="text-slate-600">
            Have questions, feedback, or need help? Choose how you&apos;d like to reach us.
          </p>
        </div>

        {/* Short-circuit power users */}
        <Card className="mb-6 border-l-4 border-l-blue-500 bg-blue-50/60">
          <CardContent className="p-4 flex items-start justify-between gap-4 flex-wrap">
            <div>
              <p className="font-medium text-slate-900">Looking for a quick answer?</p>
              <p className="text-sm text-slate-600 mt-0.5">
                Our Help Guide answers most common questions instantly.
              </p>
            </div>
            <Link to="/help">
              <Button variant="outline" className="border-blue-500 text-blue-700 hover:bg-blue-100" data-testid="contact-browse-help">
                <BookOpen className="w-4 h-4 mr-2" /> Browse Help
              </Button>
            </Link>
          </CardContent>
        </Card>

        {/* Ways to reach us — 2-column grid on ≥sm screens, single column on mobile.
            Tiles render only when their target is configured (Help + Email always;
            Support Ticket + General Inquiry conditionally). */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {tiles.map((t) => {
            const Icon = t.icon;
            const Body = (
              <Card className="h-full hover-lift border-2 border-gray-200 hover:border-[#0A7EA4] transition-all cursor-pointer">
                <CardContent className="p-5">
                  <div className={`p-2.5 ${t.iconBg} rounded-lg w-fit mb-3`}>
                    <Icon className={`w-6 h-6 ${t.iconColor}`} />
                  </div>
                  <p className="font-semibold text-slate-900">{t.title}</p>
                  <p className={`text-xs text-slate-500 mt-1 ${t.subtitleClass || ''}`}>
                    {t.subtitle}
                  </p>
                </CardContent>
              </Card>
            );

            if (t.to) {
              return (
                <Link key={t.key} to={t.to} className="block" data-testid={t.testId}>
                  {Body}
                </Link>
              );
            }
            return (
              <a
                key={t.key}
                href={t.href || undefined}
                target={t.external ? '_blank' : undefined}
                rel={t.external ? 'noopener noreferrer' : undefined}
                className={`block ${t.disabled ? 'pointer-events-none opacity-50' : ''}`}
                data-testid={t.testId}
              >
                {Body}
              </a>
            );
          })}
        </div>
      </div>
    </Layout>
  );
};

export default ContactUsPage;
